import { RootState } from "../store"
import { findNinetyDayGapDays } from "../../lib/rules/ninetyDayGap"

/**
 * Defined here rather than alongside the explainer UI components because this
 * module (business logic, shared with native) cannot depend on
 * `components/todos/todoExplainerContent` (JSX, web-only) -- that file imports
 * this type back instead. See docs/plans/native-app-sdk-seams.md, Stage 1.
 */
export interface ExplainerVars {
  provider?: string
  /** Number of days in a 90-day-gap todo. */
  gapDays?: number
}

export interface ResolvedExplainer {
  /** Vars passed to buildTodoExplainer to personalize the copy. */
  vars: ExplainerVars
  /** Where the primary "proceed" button takes the user (today's todo destination). */
  proceedTo: string
  /** Where an optional secondary action button takes the user, if the explainer offers one. */
  secondaryTo?: string
}

/**
 * Derives the explainer vars and onward destination(s) for a todo from the
 * current Redux state. This is the single place that reproduces each todo's
 * destination and personalized copy, so nothing needs to be stashed in the
 * todo's action (keeping the synced action JSON minimal). The gap-size
 * calculation reuses the same helper the generator uses, so the explainer
 * copy always matches the todo.
 */
export function resolveTodoExplainer(
  s: RootState,
  recordType: string,
  recordId: string,
  todoType: string,
): ResolvedExplainer | undefined {
  switch (`${recordType}/${todoType}`) {
    case "expense/itemized-bill": {
      const expense = s.expenses.expenses.find((e) => e.id === recordId)
      if (!expense) { return }
      return {
        vars: { provider: expense.provider ?? undefined },
        proceedTo: "/receipts",
        secondaryTo: expense.provider_id ? `/providers/${expense.provider_id}` : undefined,
      }
    }
    case "expense/prepayment-agreement-pdf": {
      const expense = s.expenses.expenses.find((e) => e.id === recordId)
      if (!expense) { return }
      return {
        vars: { provider: expense.provider ?? undefined },
        proceedTo: `/expenses/${expense.id}/edit`,
      }
    }
    case "submission/personal-responsibility": {
      const submission = s.submissions.submissions.find((sub) => sub.id === recordId)
      if (!submission) { return }
      return {
        vars: {},
        proceedTo: `/incidents/${submission.incident_id}/submit/${submission.id}/personal-responsibility`,
      }
    }
    case "incident/maternity-prepayment-agreement": {
      const incident = s.incidents.incidents?.find((i) => i.id === recordId)
      if (!incident) { return }
      return { vars: {}, proceedTo: `/incidents/${incident.id}` }
    }
    case "incident/ninety-day-gap": {
      const incident = s.incidents.incidents?.find((i) => i.id === recordId)
      if (!incident) { return }
      const incidentExpenses = s.expenses.expenses.filter((e) => e.incident_id === incident.id)
      // gapDays can be undefined if the gap has since closed (e.g. an expense was
      // added/edited between todo generation and the user tapping it) — intentionally
      // not an early-return here, since every other case in this switch only checks
      // record existence, not whether the triggering condition still holds.
      // buildTodoExplainer already degrades to generic copy for this.
      return {
        vars: { gapDays: findNinetyDayGapDays(incidentExpenses) },
        proceedTo: `/incidents/${incident.id}`,
      }
    }
    default:
      return
  }
}
